import React, { useState } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from '../../components/Layout';
import Card from '../../components/Card';

const PageTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1A2440;
  margin-bottom: 20px;
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#1A2440' : 'transparent'};
  color: ${props => props.active ? '#1A2440' : '#6c757d'};
  font-size: 16px;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #1A2440;
  }
`;

const FinancialTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
  
  th, td {
    padding: 12px 15px;
    text-align: right;
    border-bottom: 1px solid #e9ecef;
    
    &:first-child {
      text-align: left;
    }
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #1A2440;
    cursor: pointer;
    
    &:hover {
      background-color: #e9ecef;
    }
  }
  
  tr:hover {
    background-color: #f8f9fa;
  }
`;

const ChartContainer = styled.div`
  height: 300px;
  margin-bottom: 30px;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
    background-color: #ccc;
    border-radius: 12px;
    transition: all 0.3s;
    margin: 0 10px;
  }
  
  .toggle-switch:after {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: white;
    top: 3px;
    left: 3px;
    transition: all 0.3s;
  }
  
  input:checked + .toggle-switch {
    background-color: #1A2440;
  }
  
  input:checked + .toggle-switch:after {
    transform: translateX(26px);
  }
`;

const ObservationsList = styled.ul`
  padding-left: 20px;
  
  li {
    margin-bottom: 15px;
    line-height: 1.5;
  }
`;

// Sample financial data
const financialData = [
  { year: '2018', revenue: 110, grossProfit: 59, operating: 32, netIncome: 21 },
  { year: '2019', revenue: 121, grossProfit: 63, operating: 35, netIncome: 24 },
  { year: '2020', revenue: 129, grossProfit: 72, operating: 33, netIncome: 26 },
  { year: '2021', revenue: 133, grossProfit: 61, operating: 41, netIncome: 27 },
  { year: '2022', revenue: 162, grossProfit: 51, operating: 30, netIncome: 25 },
  { year: '2023e', revenue: 138, grossProfit: 41, operating: 41, netIncome: 29 },
  { year: '2024', revenue: 137, grossProfit: 55, operating: 31, netIncome: 26 },
  { year: '2025e', revenue: 146, grossProfit: 63, operating: 33, netIncome: 29 },
];

// Sample chart data
const chartData = [
  { year: '2018', revenue: 95, ebitda: 63 },
  { year: '2019', revenue: 103, ebitda: 68 },
  { year: '2020', revenue: 112, ebitda: 73 },
  { year: '2021', revenue: 116, ebitda: 78 },
  { year: '2022e', revenue: 121, ebitda: 82 },
  { year: '2023e', revenue: 128, ebitda: 88 },
  { year: '2023e', revenue: 135, ebitda: 92 },
];

const FinancialsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('historical');
  const [showExtracted, setShowExtracted] = useState(false);

  return (
    <Layout 
      companyName="Sample Company"
      sector="Software"
      location="United States"
    >
      <PageTitle>CIM Analyzer - Financials</PageTitle>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'historical'} 
          onClick={() => setActiveTab('historical')}
        >
          Income Statement
        </Tab>
        <Tab 
          active={activeTab === 'balance'} 
          onClick={() => setActiveTab('balance')}
        >
          Balance Sheet
        </Tab>
        <Tab 
          active={activeTab === 'cash'} 
          onClick={() => setActiveTab('cash')}
        >
          Cash Flow
        </Tab>
      </TabsContainer>
      
      <ToggleContainer>
        <Toggle>
          Reported
          <input 
            type="checkbox" 
            checked={showExtracted} 
            onChange={() => setShowExtracted(!showExtracted)} 
          />
          <span className="toggle-switch"></span>
          Extracted
        </Toggle>
      </ToggleContainer>
      
      <Card title="Annual Revenue and EBITDA">
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#1A2440" strokeWidth={2} />
              <Line type="monotone" dataKey="ebitda" stroke="#4CAF50" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </Card>
      
      <Card title="Key Financials">
        <FinancialTable>
          <thead>
            <tr>
              <th>Year</th>
              <th>Revenue</th>
              <th>Gross Profit</th>
              <th>Operating</th>
              <th>Net Income</th>
            </tr>
          </thead>
          <tbody>
            {financialData.map((item) => (
              <tr key={item.year}>
                <td>{item.year}</td>
                <td>{item.revenue}</td>
                <td>{item.grossProfit}</td>
                <td>{item.operating}</td>
                <td>{item.netIncome}</td>
              </tr>
            ))}
          </tbody>
        </FinancialTable>
      </Card>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <Card title="Observations">
          <ObservationsList>
            <li>Revenue has grown at a CAGR of 5.8%</li>
            <li>Gross margin has increased from 58.8% to 60.9%</li>
            <li>Net income decreased in 2021 due to adverse market conditions</li>
          </ObservationsList>
        </Card>
      </div>
    </Layout>
  );
};

export default FinancialsPage;
